import { noop } from 'lodash';
import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import { SharedChipProps } from '../../types/SharedChipProps';
import { SharedModalProps } from '../../types/SharedModalProps';
import { SharedPanelProps } from '../../types/SharedPanelProps';
import { UserAction } from '../../types/UserAction';
import { Actions } from '../Actions/Actions';
import { Button } from '../Button/Button';
import { Header } from '../Header/Header';
import { Layout } from '../Layout/Layout';
import { PageOverlay } from '../PageOverlay/PageOverlay';

interface Props extends SharedModalProps {
  /** Optional panel (only supported when `isFullPage=true` */
  panel?: SharedPanelProps;
  /** Optional informative chip for the header **/
  chips?: SharedChipProps[];
}

/**
 * `Modal` is a controlled component that renders popover content with action buttons.
 *
 * `Modal` visibiliy behaviors
 * - `isVisible` controls the visibility
 * - `onHide` callback is called when the modal is being hidden.
 * - `onCancel`, when specified, renders the `ButtonCancel`.  It also attaches this callback to the following actions:
 *   1. Cancel button
 *   2. Close icon
 *   3. `Escape` keypress
 */
export const Modal = memo(
  ({
    children,
    backAction: providedBackAction,
    backgroundGradient,
    cancelText: initialCancelText,
    chips,
    disableHideOnEscape,
    enableHideOnPrimaryAction,
    enableContentPadding,
    headerCalloutContent,
    headerSize,
    icon,
    isFullPage,
    isStretched,
    isVisible = false,
    leftActions: providedLeftActions,
    leftContent,
    loadingContent,
    placeholderContent,
    primaryAction,
    overflow,
    secondaryActions = [],
    stepper,
    disableFooter = false,
    panel,
    title,
    titleLink,
    width = 'm',
    onCancel = noop,
    onHide,
  }: Props) => {
    const intl = useIntl();
    const hasCancel = Boolean(onCancel);

    // Only if cancelText is undefined, we default to the intl message.
    const cancelText =
      initialCancelText || initialCancelText === null
        ? initialCancelText
        : intl.formatMessage({
            id: 'Modal.cancelButton',
            defaultMessage: 'Cancel',
            description: 'Cancel button label',
          });

    const handleCancel = () => {
      onCancel?.();
      onHide?.();
    };

    const header = (
      <Header
        actions={
          hasCancel
            ? [
                {
                  icon: 'x',
                  mode: 'icon',
                  text: intl.formatMessage({
                    id: 'Modal.closeButton',
                    defaultMessage: 'Close',
                    description: 'Close modal button label',
                  }),
                  tooltip: intl.formatMessage({
                    id: 'Modal.closeTooltip',
                    defaultMessage: 'Close',
                    description: 'Tooltip for close modal button',
                  }),
                  onClick: handleCancel,
                },
              ]
            : []
        }
        calloutContent={headerCalloutContent}
        icon={icon}
        iconPosition={icon?.iconPosition}
        size={isFullPage ? 'l' : (headerSize ?? 'm')}
        stepper={stepper}
        title={title}
        titleLink={titleLink}
        chips={chips}
      />
    );

    // ensures certain properties of backAction cannot be configured
    const backAction = providedBackAction
      ? {
          ...providedBackAction,
          icon: undefined,
          level: 'tertiary' as const,
        }
      : null;

    // Combine backAction and leftActions
    let leftActions: UserAction[] | null = null;
    if (backAction && providedLeftActions) {
      leftActions = [backAction, ...providedLeftActions];
    } else if (backAction) {
      leftActions = [backAction];
    } else if (providedLeftActions) {
      leftActions = providedLeftActions;
    }

    const footer = (
      // TODO: refactor to use `Footer`
      <Layout
        align="center"
        flex="none"
        h="footer.height"
        justify="space-between"
        px={6}
      >
        {leftContent}
        {leftActions ? <Actions actions={leftActions} /> : <div />}
        <Layout preset="buttons">
          {hasCancel && cancelText && (
            <Button
              text={cancelText}
              variant="tertiary"
              onClick={handleCancel}
            />
          )}
          {secondaryActions.map((secondaryAction, i) => (
            <Button key={i} {...secondaryAction} />
          ))}
          {primaryAction && (
            <Button
              variant="primary"
              {...primaryAction}
              onClick={() => {
                primaryAction.onClick?.();
                if (enableHideOnPrimaryAction) {
                  onHide?.();
                }
              }}
            />
          )}
        </Layout>
      </Layout>
    );

    return (
      <PageOverlay
        backgroundGradient={backgroundGradient}
        disableHideOnClickOutside
        disableHideOnEscape={disableHideOnEscape}
        enableContentPadding={enableContentPadding}
        footer={!disableFooter && footer}
        header={header}
        isFullPage={isFullPage}
        isStretched={isStretched}
        isVisible={isVisible}
        loadingContent={loadingContent}
        panel={panel}
        placeholderContent={placeholderContent}
        width={width}
        overflow={overflow}
        onHide={handleCancel}
      >
        {children}
      </PageOverlay>
    );
  },
);
